'use client';

import { CircleCheck, CircleX, KeyRound, LucideIcon, Mail, Mails, Smartphone, Truck, User } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import DashboardPersonalEmail from '@/components/features/dashboard/modal/DashboardPersonalEmailAddress';
import DashboardPersonalPassword from '@/components/features/dashboard/modal/DashboardPersonalPassword';
import DashboardPersonalPhoneNumber from '@/components/features/dashboard/modal/DashboardPersonalPhoneNumber';
import DashboardPersonalProfile from '@/components/features/dashboard/modal/DashboardPersonalProfile';
import DashboardPersonalShipping from '@/components/features/dashboard/modal/DashboardPersonalShipping';
import DashboardPersonalSubEmail from '@/components/features/dashboard/modal/DashboardPersonalSubEmailAddress';

const IS_SUB_EMAIL_VISIBLE = false;

export default function DashboardPersonalChipUnit() {
    const { openModal } = useModalStore();
    const userRegistrationStatus = useAuthStore((s) => s.userRegistrationStatus);

    return (
        <div className="flex w-full">
            <div className="flex flex-col w-full">
                <div className="grid grid-rows-3 grid-flow-col auto-cols-fr gap-2 w-full">
                    <DashboardPersonalChipCard
                        icon={User}
                        title="プロフィール"
                        subtitle="Profile"
                        bgColorClass="bg-color-dashboard-profile-bg"
                        textColorClass="text-color-white-base"
                        isRegistration={true}
                        onClick={() =>
                            openModal({
                                id: 'dashboard-personal-profile',
                                component: <DashboardPersonalProfile />,
                                size: {
                                    pcWidth: '900px',
                                    mbWidth: '90%',
                                    pcHeight: '620px',
                                    mbHeight: '80%',
                                },
                                direction: 'clip',
                                overFlowType: 'auto',
                                zIndex: 2500,
                            })
                        }
                    />
                    <DashboardPersonalChipCard
                        icon={Mail}
                        title="メールアドレス"
                        subtitle="Email Address"
                        bgColorClass="bg-color-dashboard-email-bg"
                        textColorClass="text-color-base"
                        isRegistration={Boolean(userRegistrationStatus?.email)}
                        onClick={() =>
                            openModal({
                                id: 'dashboard-personal-email',
                                component: <DashboardPersonalEmail />,
                                size: {
                                    pcWidth: '900px',
                                    mbWidth: '90%',
                                    pcHeight: 'min(620px, 90vh)',
                                    mbHeight: '80%',
                                },
                                direction: 'clip',
                                overFlowType: 'auto',
                                zIndex: 2500,
                            })
                        }
                    />
                    {IS_SUB_EMAIL_VISIBLE ? (
                        <DashboardPersonalChipCard
                            icon={Mails}
                            title={'サブメール\u200Bアドレス'}
                            subtitle="Sub Email Address"
                            bgColorClass="bg-color-dashboard-sub-email-bg"
                            textColorClass="text-color-white-base"
                            isRegistration={Boolean(userRegistrationStatus?.sub_email)}
                            disabled={!userRegistrationStatus?.email}
                            onClick={() =>
                                openModal({
                                    id: 'dashboard-personal-sub-email',
                                    component: <DashboardPersonalSubEmail />,
                                    size: {
                                        pcWidth: '900px',
                                        mbWidth: '90%',
                                        pcHeight: 'min(720px, 90vh)',
                                        mbHeight: '80%',
                                    },
                                    direction: 'clip',
                                    overFlowType: 'auto',
                                    zIndex: 2500,
                                })
                            }
                        />
                    ) : (
                        <div />
                    )}
                    <DashboardPersonalChipCard
                        icon={Truck}
                        title="お届け先"
                        subtitle="Shipping"
                        bgColorClass="bg-color-dashboard-shipping-bg"
                        textColorClass="text-color-white-base"
                        isRegistration={Boolean(userRegistrationStatus?.address)}
                        onClick={() =>
                            openModal({
                                id: 'dashboard-personal-shipping',
                                component: <DashboardPersonalShipping />,
                                size: {
                                    pcWidth: '900px',
                                    mbWidth: '90%',
                                    pcHeight: '620px',
                                    mbHeight: '80%',
                                },
                                direction: 'clip',
                                overFlowType: 'auto',
                                zIndex: 2500,
                            })
                        }
                    />
                    <DashboardPersonalChipCard
                        icon={Smartphone}
                        title="電話番号"
                        subtitle="Phone Number"
                        bgColorClass="bg-color-dashboard-tel-bg"
                        textColorClass="text-color-white-base"
                        isRegistration={Boolean(userRegistrationStatus?.phone_number)}
                        onClick={() =>
                            openModal({
                                id: 'dashboard-personal-phone-number',
                                component: <DashboardPersonalPhoneNumber />,
                                size: {
                                    pcWidth: '900px',
                                    mbWidth: '90%',
                                    pcHeight: '620px',
                                    mbHeight: '80%',
                                },
                                direction: 'clip',
                                overFlowType: 'auto',
                                zIndex: 2500,
                            })
                        }
                    />
                    <DashboardPersonalChipCard
                        icon={KeyRound}
                        title="パスワード"
                        subtitle="Password"
                        bgColorClass="bg-color-dashboard-password-bg"
                        textColorClass="text-color-white-base"
                        isRegistration={true}
                        onClick={() =>
                            openModal({
                                id: 'dashboard-personal-password',
                                component: <DashboardPersonalPassword />,
                                size: {
                                    pcWidth: '900px',
                                    mbWidth: '90%',
                                    pcHeight: '620px',
                                    mbHeight: '80%',
                                },
                                direction: 'clip',
                                overFlowType: 'auto',
                                zIndex: 2500,
                            })
                        }
                    />
                </div>
            </div>
        </div>
    );
}

type DashboardPersonalChipCardProps = {
    title: string;
    subtitle: string;
    bgColorClass: string;
    textColorClass: string;
    icon: LucideIcon;
    isRegistration?: boolean;
    visible?: boolean;
    disabled?: boolean;
    onClick?: () => void;
};

function DashboardPersonalChipCard({
    title,
    subtitle,
    bgColorClass,
    textColorClass,
    icon: Icon,
    isRegistration = false,
    visible = true,
    disabled = false,
    onClick,
}: DashboardPersonalChipCardProps) {
    if (!visible) return null;

    const StatusIcon = isRegistration ? CircleCheck : CircleX;
    const statusColorClass = isRegistration ? 'text-color-dashboard-check-ok' : 'text-color-dashboard-check-ng';

    return (
        <div
            className={`flex items-center gap-x-2.5 w-full h-12 rounded-xl border-[1.5px] border-color-co-gray bg-color-white-base px-3 group ${
                disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-color-service-mouseover transition-colors duration-200'
            }`}
            onClick={disabled ? undefined : onClick}
        >
            <div className={`flex items-center justify-center flex-none w-7 h-7 rounded-[9px] ${bgColorClass}`}>
                <IconAdaptive Icon={Icon} baseSize={16} strokeWidth={2} className={textColorClass} />
            </div>
            <div className="flex flex-col justify-center min-w-0">
                <div
                    className={`font-corp font-medium text-[13px] md:text-sm leading-tight break-keep break-words text-color-base ${
                        disabled ? '' : 'group-hover:text-color-service-mouseover transition-colors duration-200'
                    }`}
                >
                    {title}
                </div>
                <div
                    className={`hidden md:block font-corp font-medium text-[10px] leading-tight break-words text-color-co-gray-dark ${
                        disabled ? '' : 'group-hover:text-color-service-mouseover transition-colors duration-200'
                    }`}
                >
                    {subtitle}
                </div>
            </div>
            <div className="flex items-center justify-end ml-auto pl-2">
                <IconAdaptive
                    Icon={StatusIcon}
                    baseSize={18}
                    breakpoints={{ md: 20 }}
                    strokeWidth={2.5}
                    className={statusColorClass}
                />
            </div>
        </div>
    );
}
